import {
  EngagementTier,
  EngagementUserStreak,
  UserEngagement,
  engagementApiHandler,
} from '@api/engagementApiHandler';
import FlashOnIcon from '@assets/images/icons/flash-on-outlined.svg';
import MagicIcon from '@assets/images/icons/magic-wand.svg';
import StarBorderedIcon from '@assets/images/icons/star-border-outlined.svg';
import { ApiResponse } from '@interfaces/index';
import { MotionImage, MotionText } from '@shared/atoms';
import { LoadingBackdrop, SystemButton } from '@shared/molecules';
import { CoreLayout } from '@shared/templates';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import { ProgressPageSkeleton } from './LoadingSkeletons';
import styles from './styles.module.scss';

// Color mapping for engagement tiers
const tierColors = {
  Beginner: { color: '#FFE665', darkColor: '#d4b202' },
  Explorer: { color: '#4FF1DE', darkColor: '#069b7b' },
  Engager: { color: '#D4C6F9', darkColor: '#6048a1' },
  Achiever: { color: '#BBF484', darkColor: '#4c8f0c' },
  Champion: { color: '#FFAF83', darkColor: '#d96525' },
};

export const MyJourneyPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const nextQuestionnaireId = params.get('nextAssessmentId');
  const nextQuestionnaire = params.get('nextAssessment');
  const validatedQuestionnaireId = nextQuestionnaireId ? Number(nextQuestionnaireId) : null;
  const validatedQuestionnaire = nextQuestionnaire || null;

  const fetchCurrentEngagement = async (): Promise<ApiResponse<UserEngagement>> =>
    engagementApiHandler.getLatestUserEngagements();

  const fetchAllEngagements = async (): Promise<ApiResponse<EngagementTier[]>> =>
    engagementApiHandler.getAllEngagementTiers();

  const fetchUserStreak = async (): Promise<ApiResponse<EngagementUserStreak>> =>
    engagementApiHandler.getEngagementUserStreak();

  const {
    data: currentEngagementData,
    isLoading: currentEngagementsLoading,
    error: currentEngagementsErrors,
  } = useQuery({
    queryKey: ['fetchCurrentEngagement'],
    queryFn: fetchCurrentEngagement,
    enabled: true,
  });

  const {
    data: allEngagementsData,
    isLoading: allEngagementsDataLoading,
    error: allEngagementsErrors,
  } = useQuery({
    queryKey: ['fetchAllEngagements'],
    queryFn: fetchAllEngagements,
    enabled: true,
  });

  const {
    data: engagementUserStreak,
    isLoading: engagementUserStreakLoading,
    error: engagementUserStreakErrors,
  } = useQuery({
    queryKey: ['fetchUserStreak'],
    queryFn: fetchUserStreak,
    enabled: true,
  });

  const processedData = useMemo(() => {
    if (!currentEngagementData?.responseObject || !allEngagementsData?.responseObject) {
      return null;
    }

    const currentUserData = currentEngagementData.responseObject;
    const allTiers = allEngagementsData.responseObject;

    const currentTierIndex = allTiers.findIndex(
      (tier) => tier.tierName === currentUserData.currentTier,
    );
    const nextTier =
      currentTierIndex >= 0 && currentTierIndex < allTiers.length - 1
        ? allTiers[currentTierIndex + 1]
        : null;

    const currentTier = allTiers[currentTierIndex];
    let progressPercentage = 100;
    let pointsNeeded = 0;

    if (nextTier && currentTier) {
      const totalPointsInCurrentLevel = currentTier.maxPoints
        ? currentTier.maxPoints - currentTier.minPoints
        : 0;
      const pointsInCurrentLevel = currentUserData.totalPoints - currentTier.minPoints;
      progressPercentage =
        totalPointsInCurrentLevel > 0
          ? Math.min(100, (pointsInCurrentLevel / totalPointsInCurrentLevel) * 100)
          : 100;
      pointsNeeded = nextTier.minPoints - currentUserData.totalPoints;
    }

    return {
      currentUserData,
      allTiers,
      currentTier,
      nextTier,
      progressPercentage,
      pointsNeeded,
    };
  }, [currentEngagementData, allEngagementsData]);

  const handleTakeAssessment = () => {
    history.push(`/assessment?questionnaireId=${validatedQuestionnaireId}`);
  };

  const handleBackToHome = () => {
    history.goBack();
  };

  if (currentEngagementsLoading || allEngagementsDataLoading || engagementUserStreakLoading) {
    return (
      <CoreLayout title="My Journey" navigationType="default">
        <div className="container" role="main">
          <div className={styles.journeyContainer}>
            <ProgressPageSkeleton />
          </div>
        </div>
      </CoreLayout>
    );
  }

  if (currentEngagementsErrors || allEngagementsErrors || !processedData) {
    return (
      <CoreLayout title="Engagement" navigationType="default">
        <LoadingBackdrop text="Something didn’t go as planned — your journey data couldn’t be located." />
        <div className="container" role="main">
          <div className={styles.journeyContainer}>
            <ProgressPageSkeleton />
          </div>
        </div>
      </CoreLayout>
    );
  }

  const { currentUserData, allTiers, currentTier, nextTier, progressPercentage, pointsNeeded } =
    processedData;

  const currentTierColors = tierColors[currentUserData.currentTier as keyof typeof tierColors] || {
    color: '#f0f0f0',
    darkColor: '#828282',
  };

  return (
    <CoreLayout title="Engagement" navigationType="default">
      <div className="container" role="main">
        <div className={styles.journeyContainer}>
          <div className={styles.header}>
            <MotionText as="h2" className={styles.title} text="Your progress" />

            <button
              onClick={handleBackToHome}
              className={styles.backButton}
              aria-label="Go back to home"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="#0096FF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Go back
            </button>
          </div>

          <div
            className={styles.currentLevelCard}
            style={{ backgroundColor: currentTierColors.color }}
            aria-live="polite"
          >
            <div className={styles.levelIconWrapper}>
              <div
                className={styles.levelIcon}
                style={{ borderColor: currentTierColors.darkColor }}
                aria-hidden="true"
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 32 42"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.36 23.38L16 19.86L20.62 23.38L18.86 17.68L23.5 14H17.82L16 8.38L14.18 14H8.5L13.12 17.68L11.36 23.38ZM32 16C32 7.16 24.84 0 16 0C7.16 0 0 7.16 0 16C0 20.06 1.52 23.74 4 26.56V42L16 38L28 42V26.56C30.48 23.74 32 20.06 32 16ZM16 4C22.62 4 28 9.38 28 16C28 22.62 22.62 28 16 28C9.38 28 4 22.62 4 16C4 9.38 9.38 4 16 4ZM16 34L8 36.04V29.84C10.36 31.2 13.08 32 16 32C18.92 32 21.64 31.2 24 29.84V36.04L16 34Z"
                    fill={currentTierColors.darkColor}
                  />
                </svg>
              </div>
            </div>
            {currentEngagementData?.responseObject?.engagementTier ? (
              <div className={styles.levelInfo}>
                <MotionText
                  as="h4"
                  className={styles.levelName}
                  text={currentUserData.currentTier}
                />

                <MotionText
                  as="text"
                  className={styles.levelDescription}
                  text={currentTier?.description}
                />

                <div className={styles.statsWrapper}>
                  <div className={styles.points}>
                    <span aria-hidden="true">
                      <MotionImage src={StarBorderedIcon} alt="" className={styles.icon} />
                    </span>{' '}
                    {currentUserData.totalPoints} points
                  </div>
                  <div className={styles.streak}>
                    <span aria-hidden="true">
                      <MotionImage src={FlashOnIcon} alt="" className={styles.icon} />
                    </span>{' '}
                    {(engagementUserStreak &&
                      engagementUserStreak?.success &&
                      engagementUserStreak?.responseObject?.currentStreak) ||
                      0}{' '}
                    days streak
                  </div>
                </div>
              </div>
            ) : (
              <div>Currently there is no active tier</div>
            )}
          </div>

          <section className={styles.journeySection} aria-labelledby="journey-heading">
            <MotionText
              id="journey-heading"
              as="h4"
              className={styles.sectionTitle}
              text="Your journey"
            />

            <MotionText
              as="text"
              className={styles.sectionDescription}
              text="Your engagement journey"
            />

            <div className={styles.journeyMap} role="list" aria-label="Engagement journey levels">
              {allTiers.map((tier) => {
                const isCurrent = tier.tierName === currentUserData.currentTier;
                const isCompleted = currentUserData.totalPoints >= tier.minPoints;
                const colors = tierColors[tier.tierName as keyof typeof tierColors] || {
                  color: '#f0f0f0',
                  darkColor: '#828282',
                };

                return (
                  <div
                    key={tier.tierName}
                    className={`${styles.journeyItem} ${isCurrent ? styles.currentLevel : ''}`}
                    role="listitem"
                    aria-current={isCurrent ? 'true' : 'false'}
                  >
                    <div
                      className={styles.journeyIcon}
                      style={{
                        backgroundColor: isCompleted ? colors.color : '#f0f0f0',
                        transform: isCurrent ? 'scale(1.15)' : 'scale(1)',
                      }}
                      aria-hidden="true"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 32 42"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.36 23.38L16 19.86L20.62 23.38L18.86 17.68L23.5 14H17.82L16 8.38L14.18 14H8.5L13.12 17.68L11.36 23.38ZM32 16C32 7.16 24.84 0 16 0C7.16 0 0 7.16 0 16C0 20.06 1.52 23.74 4 26.56V42L16 38L28 42V26.56C30.48 23.74 32 20.06 32 16ZM16 4C22.62 4 28 9.38 28 16C28 22.62 22.62 28 16 28C9.38 28 4 22.62 4 16C4 9.38 9.38 4 16 4ZM16 34L8 36.04V29.84C10.36 31.2 13.08 32 16 32C18.92 32 21.64 31.2 24 29.84V36.04L16 34Z"
                          fill={colors.darkColor}
                        />
                      </svg>
                    </div>
                    <div className={styles.journeyItemTitle}>{tier.tierName}</div>
                    <div className={styles.journeyItemPoints}>{tier.minPoints}+ points</div>
                  </div>
                );
              })}
            </div>

            {nextTier && (
              <div className={styles.progressBarSection}>
                <div className={styles.progressLabel}>
                  <MotionText as="h5" text={`Progress to ${nextTier.tierName}`} />

                  <span>{pointsNeeded} points needed</span>
                </div>
                <div
                  className={styles.progressBarContainer}
                  role="progressbar"
                  aria-valuenow={progressPercentage}
                  aria-label={`Progress to ${nextTier.tierName}: ${progressPercentage.toFixed(0)}%`}
                >
                  <div className={styles.progressBar} style={{ width: `${progressPercentage}%` }} />
                </div>
                <div className={styles.progressPoints}>
                  <span>{currentUserData.totalPoints} points</span>
                  <span>{nextTier.minPoints} points</span>
                </div>
              </div>
            )}
          </section>

          <section className={styles.allTiersSection} aria-labelledby="all-tiers-heading">
            <MotionText
              id="all-tiers-heading"
              as="h4"
              className={styles.sectionTitle}
              text="All tiers"
            />

            <div className={styles.tiersGrid} role="list" aria-label="All engagement tiers">
              {allTiers.map((tier) => {
                const isCurrent = tier.tierName === currentUserData.currentTier;
                const isCompleted = currentUserData.totalPoints >= tier.minPoints;
                const colors = tierColors[tier.tierName as keyof typeof tierColors] || {
                  color: '#f0f0f0',
                  darkColor: '#828282',
                };

                return (
                  <div
                    key={tier.tierName}
                    className={`${styles.tierCard} ${isCurrent ? styles.currentTier : ''}`}
                    style={{ borderColor: isCurrent ? colors.darkColor : '#f4f2ec' }}
                    role="listitem"
                    aria-current={isCurrent ? 'true' : 'false'}
                  >
                    <div className={styles.tierHeader}>
                      <div
                        className={styles.tierIcon}
                        style={{ backgroundColor: isCompleted ? colors.color : '#f4f2ec' }}
                        aria-hidden="true"
                      >
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 32 42"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M11.36 23.38L16 19.86L20.62 23.38L18.86 17.68L23.5 14H17.82L16 8.38L14.18 14H8.5L13.12 17.68L11.36 23.38ZM32 16C32 7.16 24.84 0 16 0C7.16 0 0 7.16 0 16C0 20.06 1.52 23.74 4 26.56V42L16 38L28 42V26.56C30.48 23.74 32 20.06 32 16ZM16 4C22.62 4 28 9.38 28 16C28 22.62 22.62 28 16 28C9.38 28 4 22.62 4 16C4 9.38 9.38 4 16 4ZM16 34L8 36.04V29.84C10.36 31.2 13.08 32 16 32C18.92 32 21.64 31.2 24 29.84V36.04L16 34Z"
                            fill={colors.darkColor}
                          />
                        </svg>
                      </div>
                      <MotionText
                        as="h4"
                        className={styles.tierName}
                        style={{ color: colors.darkColor }}
                        text={tier.tierName}
                      />
                    </div>
                    <div className={styles.tierPoints}>{tier.minPoints}+ points</div>
                    <MotionText
                      as="text"
                      className={styles.tierDescription}
                      text={tier.description}
                    />
                  </div>
                );
              })}
            </div>
          </section>

          {validatedQuestionnaireId && (
            <section className={styles.keepGoingSection} aria-labelledby="keep-going-heading">
              <div className={styles.keepGoingContent}>
                <div className={styles.keepGoingIcon} aria-hidden="true">
                  <MotionImage
                    src={MagicIcon}
                    alt=""
                    aria-hidden="true"
                    className={styles.magicIcon}
                  />
                </div>
                <MotionText
                  id="keep-going-heading"
                  as="h4"
                  className={styles.keepGoingTitle}
                  text="Keep going"
                />

                <MotionText
                  as="text"
                  className={styles.keepGoingText}
                  text={`Complete ${
                    validatedQuestionnaire || 'next'
                  } assessment to unlock more insights.`}
                />

                <SystemButton
                  className={styles.assessmentButton}
                  onClick={handleTakeAssessment}
                  aria-label="Take assessment"
                  size="small"
                >
                  Take {validatedQuestionnaire || 'Next'} Assessment
                </SystemButton>
              </div>
            </section>
          )}
        </div>
      </div>
    </CoreLayout>
  );
};
